import { ErrorState } from '@/components/primitives/ErrorState'
import { PageHeader } from '@/components/primitives/PageHeader'
import {
  btnPrimary,
  btnSecondary,
  iconBtn
} from '@/components/record-sheet/styles'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import {
  type AccountConnection,
  type ChatAgent,
  connectionAction,
  fetchConnections,
  updateConnection
} from '@/lib/ai-team'
import { notifyError, queryClient } from '@/lib/query'
import { ToolLogo } from '@/lib/tool-icons'
import { cn } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { Loader2, RefreshCw, Search, Unplug, Users } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

export function ConnectionsPage() {
  const [params, setParams] = useSearchParams()
  const [search, setSearch] = useState('')
  const query = useQuery({
    queryKey: ['ai-connections'],
    queryFn: fetchConnections
  })

  // Back from the provider's sign-in page: check the account straight away.
  const returned = params.get('connected')
  useEffect(() => {
    if (!returned) return
    connectionAction(returned, 'refresh')
      .then((c) => {
        if (c.status === 'active') toast.success(`${c.name} is connected.`)
        else
          toast.error(
            `${c.name}: ${c.status_label}${c.error ? ` — ${c.error}` : ''}`
          )
      })
      .catch((err) => notifyError('Could not check the connection', err))
      .finally(() => {
        queryClient.invalidateQueries({ queryKey: ['ai-connections'] })
        setParams({}, { replace: true })
      })
  }, [returned, setParams])

  const groups = useMemo(() => {
    const data = query.data
    if (!data) return []
    const q = search.trim().toLowerCase()
    return data.categories
      .map((cat) => ({
        cat,
        rows: data.connections.filter(
          (c) => c.category === cat && (!q || c.name.toLowerCase().includes(q))
        )
      }))
      .filter((g) => g.rows.length > 0)
  }, [query.data, search])

  const connected =
    query.data?.connections.filter((c) => c.status === 'active').length ?? 0

  return (
    <div className="space-y-6">
      <PageHeader
        title="Connected accounts"
        description="XOBLACK's own accounts outside the platform, linked through Composio for the AI team. Reading is instant; anything that posts, sends, signs, books or spends comes to you as a card first."
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex min-w-60 flex-1 items-center gap-2 rounded-[36px] border border-neutral-200 bg-white px-4 py-2 dark:border-neutral-800 dark:bg-neutral-900">
          <Search className="size-4 text-neutral-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search accounts…"
            aria-label="Search accounts"
            className="w-full bg-transparent text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none dark:text-white"
          />
        </div>
        {query.data && (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            {connected} of {query.data.connections.length} connected
          </p>
        )}
      </div>

      {query.isLoading && (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-40 animate-pulse rounded-[36px] bg-neutral-200 dark:bg-neutral-800"
            />
          ))}
        </div>
      )}
      {query.error && (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      )}
      {query.data && groups.length === 0 && (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          No accounts match "{search}".
        </p>
      )}

      {groups.map((g) => (
        <section key={g.cat} className="space-y-3">
          <h2 className="px-1 text-xs font-semibold text-neutral-500 dark:text-neutral-400">
            {g.cat}
            <span className="ml-2 font-normal">
              {g.rows.filter((r) => r.status === 'active').length}/
              {g.rows.length} connected
            </span>
          </h2>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {g.rows.map((c) => (
              <ConnectionCard
                key={c.toolkit}
                conn={c}
                agents={query.data?.agents ?? []}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}

function ConnectionCard({
  conn,
  agents
}: {
  conn: AccountConnection
  agents: ChatAgent[]
}) {
  const [pending, setPending] = useState<string | null>(null)
  const names = new Map(agents.map((a) => [a.key, a.name]))
  const connected = conn.status === 'active'
  const usedBy = conn.agents.map((k) => names.get(k) ?? k)

  async function run(what: string, fn: () => Promise<unknown>) {
    setPending(what)
    try {
      await fn()
      await queryClient.invalidateQueries({ queryKey: ['ai-connections'] })
    } catch (err) {
      notifyError(`${conn.name}: could not ${what}`, err)
    } finally {
      setPending(null)
    }
  }

  async function connect() {
    setPending('connect')
    try {
      const res = await connectionAction(conn.toolkit, 'connect')
      if (res.redirect_url) window.location.href = res.redirect_url
    } catch (err) {
      notifyError(`${conn.name}: could not start sign-in`, err)
      setPending(null)
    }
  }

  function toggleAgent(key: string) {
    const next = conn.agents.includes(key)
      ? conn.agents.filter((k) => k !== key)
      : [...conn.agents, key]
    run('save', () => updateConnection(conn.toolkit, { agents: next }))
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-4 rounded-[36px] border bg-white p-5 transition-colors dark:bg-neutral-900',
        connected
          ? 'border-emerald-200 dark:border-emerald-500/30'
          : 'border-neutral-200 dark:border-neutral-800'
      )}
    >
      <div className="flex items-start gap-3">
        <ToolLogo toolkit={conn.toolkit} size="lg" />
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-neutral-900 dark:text-white">
            {conn.name}
          </p>
          <p className="mt-0.5 flex items-center gap-1.5 text-xs text-neutral-500 dark:text-neutral-400">
            <span
              className={cn(
                'size-1.5 rounded-full',
                connected
                  ? 'bg-emerald-500'
                  : conn.status === 'error'
                    ? 'bg-red-500'
                    : conn.status === 'pending'
                      ? 'bg-amber-500'
                      : 'bg-neutral-300 dark:bg-neutral-600'
              )}
            />
            {conn.status_label}
            {conn.money && (
              <span className="text-red-600 dark:text-red-400">
                · Spends money
              </span>
            )}
          </p>
        </div>
      </div>

      <p className="line-clamp-2 min-h-8 text-xs text-neutral-500 dark:text-neutral-400">
        {usedBy.length
          ? `Used by ${usedBy.join(', ')}`
          : 'No agent can use it yet.'}
      </p>

      {conn.error && (
        <p className="text-xs text-red-600 dark:text-red-400">{conn.error}</p>
      )}

      <div className="mt-auto flex items-center gap-2">
        {connected ? (
          <>
            <button
              type="button"
              className={cn(btnSecondary, 'flex-1 justify-center py-1.5')}
              disabled={!!pending}
              onClick={() =>
                run('refresh', () => connectionAction(conn.toolkit, 'refresh'))
              }
            >
              {pending === 'refresh' ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <RefreshCw className="size-4" />
              )}
              Check
            </button>
            <button
              type="button"
              className={iconBtn}
              disabled={!!pending}
              onClick={() =>
                run('disconnect', () =>
                  connectionAction(conn.toolkit, 'disconnect')
                )
              }
              aria-label={`Disconnect ${conn.name}`}
              title="Disconnect"
            >
              <Unplug className="size-4" />
            </button>
          </>
        ) : (
          <button
            type="button"
            className={cn(btnPrimary, 'flex-1 justify-center py-1.5')}
            disabled={!!pending}
            onClick={connect}
          >
            {pending === 'connect' && (
              <Loader2 className="size-4 animate-spin" />
            )}
            {conn.status === 'not_connected' ? 'Connect' : 'Connect again'}
          </button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={iconBtn}
              aria-label={`Choose which agents use ${conn.name}`}
              title="Choose agents"
            >
              <Users className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="max-h-[50vh] w-60 overflow-auto rounded-[36px]"
          >
            <DropdownMenuLabel>
              Agents who can use {conn.name}
            </DropdownMenuLabel>
            {agents.map((a) => (
              <DropdownMenuCheckboxItem
                key={a.key}
                checked={conn.agents.includes(a.key)}
                onSelect={(e) => e.preventDefault()}
                onCheckedChange={() => toggleAgent(a.key)}
                disabled={!!pending}
              >
                {a.name}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}
